import { type Dispatch, type SetStateAction } from 'react';

import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  type ModalProps,
  Stack,
  Switch,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { type OptionBase } from 'chakra-react-select';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Form,
  FormField,
  IconChevronRight,
  IconMail,
  SlideIn,
} from '@/components';
import { CircularIconContainer } from '@/components/Icons/IconContainer/';
import { useShareCandidateMutation } from '@/features/candidate/useCandidateTableMutation';
import { type CandidateBasicDto } from '@/services/api';
import { getFullname } from '@/utils/identity';

import {
  type FormFieldsShareCandidates,
  zFormFieldsShareCandidates,
} from './schemas';

interface AddNewCandidateModalProps extends Omit<ModalProps, 'children'> {
  interviewId?: string;
  selectedCandidates: Array<CandidateBasicDto>;
  setSelectedCandidates: Dispatch<SetStateAction<Array<CandidateBasicDto>>>;
  allCandidates: Array<CandidateBasicDto>;
}

interface CandidateOption extends OptionBase {
  label: string;
  value: string;
}

const toOptions = (candidate: CandidateBasicDto): CandidateOption => {
  const fullName = getFullname(candidate.name, candidate.surname);

  return {
    label: fullName,
    value: candidate.id,
  };
};

export const ShareCandidateModal: React.FC<AddNewCandidateModalProps> = ({
  isOpen,
  onClose,
  interviewId,
  selectedCandidates,
  setSelectedCandidates,
  allCandidates,
}) => {
  const toast = useToast();
  const { t } = useTranslation(['common', 'candidate']);

  const shareCandidateWithEmail = useShareCandidateMutation({
    onSuccess() {
      toast({
        title: t('candidate:share.successMessage'),
        status: 'success',
      });
      form.reset();
      onClose();
      setSelectedCandidates([]);
    },
    onError() {
      toast({
        title: t('candidate:share.errorMessage'),
        status: 'error',
      });
    },
  });

  const form = useForm<FormFieldsShareCandidates>({
    mode: 'onBlur',
    resolver: zodResolver(zFormFieldsShareCandidates()),
    defaultValues: {
      nameSurname: '',
      email: '',
      description: '',
      selectedCandidates: [],
      isHideUserInfo: false,
    },
    values: {
      selectedCandidates: selectedCandidates.map((c) => c.id) as [
        string,
        ...Array<string>,
      ],
      nameSurname: '',
      email: '',
      description: '',
      isHideUserInfo: false,
    },
  });

  const onSubmit: SubmitHandler<FormFieldsShareCandidates> = async (data) => {
    if (!interviewId) return;

    await shareCandidateWithEmail.mutateAsync({
      interviewId,
      body: {
        candidateIds: data.selectedCandidates,
        email: data.email,
        body: data.description,
        fullName: data.nameSurname,
        isHideUserInfo: data.isHideUserInfo ?? false,
      },
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        form.reset();
        onClose();
      }}
    >
      <ModalOverlay />
      <ModalContent w="420px" borderRadius="xl" pt={6} alignItems="center">
        <ModalCloseButton
          borderRadius="full"
          borderWidth="1px"
          borderColor="gray.100"
        />
        <ModalHeader justifyItems="center">
          <VStack>
            <CircularIconContainer icon={IconMail} />
            <Text>{t('candidate:share.modalTitle')}</Text>
          </VStack>
        </ModalHeader>
        <ModalBody w="full" pb={2}>
          <SlideIn>
            <Form {...form} onSubmit={onSubmit}>
              <Stack spacing={4}>
                <FormField
                  type="text"
                  label={t('candidate:share.nameSurnameLabel')}
                  control={form.control}
                  name="nameSurname"
                  optionalityHint="required"
                  size="lg"
                  placeholder={t('candidate:share.nameSurnameLabel')}
                />
                <FormField
                  type="email"
                  label={t('candidate:share.emailLabel')}
                  control={form.control}
                  name="email"
                  optionalityHint="required"
                  size="lg"
                  placeholder={t('candidate:share.emailPlaceholder')}
                />
                <FormField
                  control={form.control}
                  name="description"
                  label={t('candidate:share.descriptionLabel')}
                  type="markdown"
                  placeholder={t('candidate:share.descriptionPlaceholder')}
                />

                <FormField
                  type="multi-select"
                  label={t('candidate:share.selectedCandidates')}
                  control={form.control}
                  closeMenuOnSelect={false}
                  name="selectedCandidates"
                  options={allCandidates.map(toOptions)}
                  placeholder={t('candidate:share.selectCandidates')}
                />

                <HStack spacing={3} align="center">
                  <Controller
                    name="isHideUserInfo"
                    control={form.control}
                    render={({ field: { onChange, value } }) => (
                      <Switch
                        isChecked={value}
                        onChange={onChange}
                        colorScheme="purple"
                      />
                    )}
                  />
                  <Text fontSize="sm" color="gray.700">
                    {t('candidate:share.hideUserInfo')}
                  </Text>
                </HStack>

                <Button
                  type="submit"
                  mb={8}
                  mt={4}
                  isLoading={shareCandidateWithEmail.isPending}
                  variant="@primary"
                  rightIcon={<IconChevronRight fontSize="lg" />}
                  w="full"
                >
                  {t('common:actions.send')}
                </Button>
              </Stack>
            </Form>
          </SlideIn>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
